import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { FaMinusCircle, FaPlusCircle } from "react-icons/fa";
import {
  useTable,
  useRowSelect,
  useExpanded,
  usePagination
} from "react-table";
import { noDataPresent, responseStatus } from "../../../../../../../locales/en";
import "./../../../../reportable/reportable.scss";
import TableSkeleton from "../../../../../../skeletons/TableSkeleton";
import { useVisibleColumnCount } from "../../../../../../../hooks/useVisibleColumnCount";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import DualPagination from "../../../../../../common/DualPagination";
import { getUserKPIReportModalData } from "../../../../../../../redux/userReportsSlice";
import { showToast } from "../../../../../../../utils/utils";
import {
  FUNCTION,
  PAGE_INDEX,
  PAGE_SIZE,
  showToastType
} from "../../../../../../../utils/constants/appConstants";

const MonthlyKpiReportCustomerTable = (props) => {
  const dispatch = useDispatch();
  const {
    invoiceIdsListRef,
    filterType,
    columnsConfig,
    detailsKey,
    linkColumnId,
    buildLink
  } = props;
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();
  const { kpiReportModalDataLoader } = useSelector((state) => state.userReport);
  const initialCustomerModalData = null;
  const [customerModalData, setCustomerModalData] = useState(
    initialCustomerModalData
  );

  const columns = useMemo(() => columnsConfig, [columnsConfig]);

  const invoiceDataIds = invoiceIdsListRef.current;
  const tableData = useMemo(
    () =>
      Array.isArray(customerModalData?.[detailsKey])
        ? customerModalData[detailsKey]
        : [],
    [customerModalData?.[detailsKey]]
  );

  const [expandedRows, setExpandedRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    state,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions
  } = useTable(
    {
      columns,
      data: tableData,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE }
    },

    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [...columns]);
    }
  );

  const { pageIndex } = state;

  const toggleRow = (rowId) => {
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  useEffect(() => {
    const handleInvoiceModalData = async () => {
      try {
        const paramsData = {
          ids: invoiceDataIds,
          filterType,
          page: pageIndex,
          size: pageSize
        };
        const response = await dispatch(
          getUserKPIReportModalData(paramsData)
        ).unwrap();
        if (response) {
          setCustomerModalData(response?.data || response);
          setTotalPages(response?.pageInfo?.totalPages || 0);
        }
      } catch (error) {
        showToast(
          error.response?.data?.message || responseStatus.errorText,
          showToastType.error
        );
      }
    };
    handleInvoiceModalData();
  }, [invoiceDataIds, pageIndex, pageSize, dispatch]);

  return (
    <div className="col-md-12">
      <table
        className={`mt-2 mb-3 reportable1 twoDatatable bgless_table`}
        {...getTableProps()}
      >
        <thead ref={headerRef}>
          {headerGroups.map((headerGroup) => {
            const { key, ...rest } = headerGroup.getHeaderGroupProps();
            return (
              <tr key={key} className="parentrow" {...rest}>
                <th />
                {headerGroup.headers.map((column) => (
                  <th key={column.id} {...column.getHeaderProps()}>
                    <span>{column.render("Header")}</span>
                  </th>
                ))}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {kpiReportModalDataLoader && (
            <TableSkeleton rows={10} columns={visibleColumnCount} />
          )}
          {!kpiReportModalDataLoader && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length + 2} className="text-center">
                {noDataPresent}
              </td>
            </tr>
          )}
          {!kpiReportModalDataLoader &&
            rows.length > 0 &&
            rows.map((row) => {
              prepareRow(row);
              const isExpanded = expandedRows.includes(row.id);
              return (
                <React.Fragment key={row.id}>
                  <tr className="parentrow" {...row.getRowProps()}>
                    <td className="expand-td">
                      <span
                        className="expandbtn cursor-pointer"
                        onClick={() => toggleRow(row.id)}
                      >
                        {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                      </span>
                    </td>
                    {row.cells.map((cell) => {
                      let content = cell.render("Cell");
                      if (
                        linkColumnId &&
                        cell.column.id === linkColumnId &&
                        typeof buildLink === FUNCTION
                      ) {
                        content = (
                          <Link to={buildLink(cell.row.original.id)}>
                            {cell.render("Cell")}
                          </Link>
                        );
                      }
                      return (
                        <td
                          key={cell.getCellProps().key}
                          {...cell.getCellProps()}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                  {isExpanded && (
                    <tr className="childrow colexpand">
                      <td colSpan={columns.length + 2}>
                        <div className="tdbody">
                          {columns.map((column) => (
                            <span
                              key={column.accessor}
                              data-label={column.Header}
                            >
                              {String(row.original[column.accessor])}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>
      {tableData && tableData.length > 0 && (
        <DualPagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
          dataLength={tableData.length}
        />
      )}
    </div>
  );
};

MonthlyKpiReportCustomerTable.propTypes = {
  invoiceIdsListRef: PropTypes.object.isRequired,
  columnsConfig: PropTypes.array.isRequired,
  detailsKey: PropTypes.string.isRequired,
  linkColumnId: PropTypes.string,
  buildLink: PropTypes.func,
  filterType: PropTypes.string
};
export default MonthlyKpiReportCustomerTable;
